import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { APIContext } from '../Contexts/APIContext';
import ChangeSiteReferenceDialog from './Maintainance/ChangeSiteReferenceDialog';
import CreateDataVersionDialog from './Maintainance/CreateDataVersionDialog';
import CreateSiteDialog from './Maintainance/CreateSiteDialog';
import BulkDeleteSitesDialog from './Maintainance/BulkDeleteSitesDialog';
import DeleteSiteDialog from './Maintainance/DeleteSiteDialog';
import MoveSitesToNextVersionDialog from './Maintainance/MoveSitesToNextVersionDialog';
import MoveSelectedSitesDialog from './Maintainance/MoveSelectedSitesDialog';
import NewSitesByVersionPanel from './Maintainance/NewSitesByVersionPanel';
import { ReleaseVersion } from './Maintainance/ReleaseManagementTypes';

type ActionCardProps = {
    title: string;
    description: string;
    actionLabel: string;
    icon: ReactNode;
    onClick: () => void;
    tone?: 'primary' | 'error';
};

function ActionCard({
    title,
    description,
    actionLabel,
    icon,
    onClick,
    tone = 'primary'
}: ActionCardProps) {
    return <Paper
        variant="outlined"
        sx={{
            p: 2.25,
            height: '100%',
            borderRadius: 3
        }}
    >
        <Stack spacing={2} height="100%">
            <Box sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                color: `${tone}.main`,
                bgcolor: theme => alpha(theme.palette[tone].main, 0.1)
            }}>
                {icon}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>{title}</Typography>
                <Typography variant="body2" color="text.secondary">{description}</Typography>
            </Box>
            <Button
                variant={tone === 'error' ? 'outlined' : 'text'}
                color={tone}
                onClick={onClick}
                endIcon={<ArrowForwardIcon />}
                sx={{ alignSelf: 'flex-start', px: tone === 'error' ? 1.5 : 0.5 }}
            >
                {actionLabel}
            </Button>
        </Stack>
    </Paper>;
}

const displayDate = (value: string) => new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
});

export default function SuperadminDashboard() {
    const theme = useTheme();
    const { get } = useContext(APIContext);
    const [openChangeReference, setOpenChangeReference] = useState(false);
    const [openCreateVersion, setOpenCreateVersion] = useState(false);
    const [openMoveSites, setOpenMoveSites] = useState(false);
    const [openCreateSite, setOpenCreateSite] = useState(false);
    const [openDeleteSite, setOpenDeleteSite] = useState(false);
    const [openMoveSelectedSites, setOpenMoveSelectedSites] = useState(false);
    const [openBulkDeleteSites, setOpenBulkDeleteSites] = useState(false);
    const [versions, setVersions] = useState<ReleaseVersion[]>([]);
    const [releaseDataRevision, setReleaseDataRevision] = useState(0);

    useEffect(() => {
        get('Admin', 'GetReleaseVersions', setVersions);
    }, [releaseDataRevision]);

    const currentVersion = versions.find(version => version.isCurrent);
    const futureVersions = versions.filter(version => new Date(version.start).getTime() > Date.now());
    const dataChanged = () => setReleaseDataRevision(value => value + 1);

    return <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 4 }}>
        <Paper
            elevation={0}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 2.5, md: 3.5 },
                mb: 3,
                borderRadius: 4,
                color: 'primary.contrastText',
                background: `linear-gradient(125deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 72%, ${theme.palette.secondary.main} 145%)`,
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: 260,
                    height: 260,
                    borderRadius: '50%',
                    right: -80,
                    top: -145,
                    bgcolor: alpha(theme.palette.common.white, 0.1)
                }
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ md: 'center' }}
                spacing={2}
                sx={{ position: 'relative', zIndex: 1 }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 3,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: alpha(theme.palette.common.white, 0.15),
                        backdropFilter: 'blur(6px)'
                    }}>
                        <AdminPanelSettingsOutlinedIcon fontSize="large" />
                    </Box>
                    <Box>
                        <Typography variant="h4">Super Admin</Typography>
                        <Typography sx={{ mt: 0.5, color: alpha(theme.palette.common.white, 0.78) }}>
                            Maintain site data and prepare upcoming TRICS releases.
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                        label={currentVersion ? `Current: ${currentVersion.versionName}` : 'No current version'}
                        sx={{ color: 'inherit', bgcolor: alpha(theme.palette.common.white, 0.14) }}
                    />
                    <Chip
                        label={`${futureVersions.length} upcoming release${futureVersions.length === 1 ? '' : 's'}`}
                        sx={{ color: 'inherit', bgcolor: alpha(theme.palette.common.white, 0.14) }}
                    />
                </Stack>
            </Stack>
        </Paper>

        <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Site management</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create and maintain version-specific site records.
            </Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <ActionCard
                        title="Create site"
                        description="Add a new site to a selected data version and continue directly to its record."
                        actionLabel="Create a site"
                        icon={<AddBusinessOutlinedIcon />}
                        onClick={() => setOpenCreateSite(true)}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <ActionCard
                        title="Change site reference"
                        description="Move a site and its related records to a new reference within one version."
                        actionLabel="Change reference"
                        icon={<DriveFileRenameOutlineOutlinedIcon />}
                        onClick={() => setOpenChangeReference(true)}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <ActionCard
                        title="Delete site"
                        description="Permanently remove a site and all related records from one selected version."
                        actionLabel="Review deletion"
                        icon={<DeleteForeverOutlinedIcon />}
                        tone="error"
                        onClick={() => setOpenDeleteSite(true)}
                    />
                </Grid>
            </Grid>
        </Box>

        <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Release tools</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Prepare data versions and schedule controlled site migrations.
            </Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <ActionCard
                        title="Create data version"
                        description="Set up a future release with its own name and activation date."
                        actionLabel="Create version"
                        icon={<CalendarMonthOutlinedIcon />}
                        onClick={() => setOpenCreateVersion(true)}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <ActionCard
                        title="Copy sites to next version"
                        description="Review missing sites and schedule a retry-safe overnight copy to the next release."
                        actionLabel="Open migration tool"
                        icon={<ContentCopyOutlinedIcon />}
                        onClick={() => setOpenMoveSites(true)}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <ActionCard
                        title="Copy or move selected sites"
                        description="Upload a CSV and either copy sites or safely move them between future data versions."
                        actionLabel="Upload site list"
                        icon={<ContentCopyOutlinedIcon />}
                        onClick={() => setOpenMoveSelectedSites(true)}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <ActionCard
                        title="Delete selected sites"
                        description="Upload a strictly validated CSV and schedule permanent deletion from one future version."
                        actionLabel="Review bulk deletion"
                        icon={<DeleteForeverOutlinedIcon />}
                        tone="error"
                        onClick={() => setOpenBulkDeleteSites(true)}
                    />
                </Grid>
            </Grid>
        </Box>

        <Grid container spacing={2}>
            <Grid size={{ xs: 12, xl: 8 }}>
                <NewSitesByVersionPanel versions={versions} revision={releaseDataRevision} />
            </Grid>
            <Grid size={{ xs: 12, xl: 4 }}>
                <Paper variant="outlined" sx={{ borderRadius: 3, height: '100%', overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h6">Data versions</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Current and scheduled releases at a glance.
                        </Typography>
                    </Box>
                    <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                        {versions.slice(0, 6).map(version => <Stack
                            key={version.id}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                            sx={{ px: 2.5, py: 1.75 }}
                        >
                            <Box>
                                <Typography fontWeight={600}>{version.versionName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {displayDate(version.start)}
                                </Typography>
                            </Box>
                            <Chip
                                size="small"
                                label={version.isCurrent
                                    ? 'Current'
                                    : new Date(version.start).getTime() > Date.now() ? 'Upcoming' : 'Previous'}
                                color={version.isCurrent
                                    ? 'success'
                                    : new Date(version.start).getTime() > Date.now() ? 'info' : 'default'}
                                variant={version.isCurrent ? 'filled' : 'outlined'}
                            />
                        </Stack>)}
                        {versions.length === 0 && <Typography color="text.secondary" sx={{ p: 2.5 }}>
                            Loading data versions…
                        </Typography>}
                    </Stack>
                </Paper>
            </Grid>
        </Grid>

        <ChangeSiteReferenceDialog
            open={openChangeReference}
            onClose={() => setOpenChangeReference(false)}
        />
        <CreateDataVersionDialog
            open={openCreateVersion}
            onClose={() => setOpenCreateVersion(false)}
            onCreated={dataChanged}
        />
        <MoveSitesToNextVersionDialog
            key={`move-sites-${releaseDataRevision}`}
            open={openMoveSites}
            onClose={() => setOpenMoveSites(false)}
        />
        <CreateSiteDialog
            open={openCreateSite}
            onClose={() => setOpenCreateSite(false)}
            onCreated={dataChanged}
        />
        <DeleteSiteDialog
            open={openDeleteSite}
            onClose={() => setOpenDeleteSite(false)}
            onDeleted={dataChanged}
        />
        <MoveSelectedSitesDialog
            open={openMoveSelectedSites}
            onClose={() => setOpenMoveSelectedSites(false)}
        />
        <BulkDeleteSitesDialog
            open={openBulkDeleteSites}
            onClose={() => setOpenBulkDeleteSites(false)}
        />
    </Box>;
}
